/**
 * Thin fetch wrapper for Omni API calls.
 *
 * Retargeted for Omni Cloud (2026-07 adoption): the original OmniBeta
 * client used session cookies (`credentials: 'include'`) validated by
 * Express session + passport-azure-ad. Omni Cloud's API is Bearer-token
 * based (MSAL access tokens), so every request now acquires a token via
 * acquireApiToken() and sends it as `Authorization: Bearer <token>`.
 * Requests are also resolved against API_BASE_URL since there is no
 * same-origin dev proxy guarantee in all environments.
 *
 * Responsibilities:
 *  - Attach a fresh MSAL access token to every request.
 *  - Default to JSON request/response handling.
 *  - On 401, trigger an MSAL interactive re-login (replaces the beta's
 *    `/auth/logout` cookie-session redirect). Components can subscribe via
 *    `onUnauthorized` if they want to clean up first.
 *  - Normalise non-2xx responses into thrown `ApiError` instances.
 *  - Provide a streaming entrypoint for SSE consumers (see api/sse.js).
 */

import { API_BASE_URL } from '../auth/config.js';
import { acquireApiToken, redirectToLogin } from '../auth/msal.js';

const DEFAULT_HEADERS = {
  Accept: 'application/json'
};

const unauthorizedSubscribers = new Set();

/**
 * Subscribe to global 401 events. Returns an unsubscribe function.
 *
 * @param {() => void} fn
 * @returns {() => void}
 */
export function onUnauthorized(fn) {
  unauthorizedSubscribers.add(fn);
  return () => unauthorizedSubscribers.delete(fn);
}

function notifyUnauthorized() {
  for (const fn of unauthorizedSubscribers) {
    try {
      fn();
    } catch (err) {
      // Subscriber errors must not block the redirect.
      console.error('onUnauthorized subscriber threw:', err);
    }
  }
  // Omni Cloud auth model: interactive MSAL re-login instead of the beta's
  // session-cookie `/auth/logout` redirect.
  void redirectToLogin();
}

/** Resolve a path against the configured API base URL. */
function resolveUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * The request was INTERCEPTED FOR APPROVAL, not performed.
 *
 * The API answers 202 for this — accepted and waiting, deliberately not 403,
 * because the operator's action was not refused. But 202 is a 2xx, so without
 * this every existing caller sails past it and toasts its own success message:
 * "Account disabled successfully" for an account that is still enabled, with a
 * request sitting waiting for somebody to approve it. A FALSE SUCCESS IS FAR
 * WORSE THAN AN ERROR HERE — the operator walks away believing the account is
 * off.
 *
 * So it is THROWN, which no caller can ignore, and every existing catch block
 * shows the server's own sentence. Callers that want to phrase it properly
 * check `approvalRequired` and use notifyApproval() instead of an error toast.
 */
export class ApprovalRequiredError extends Error {
  /**
   * @param {string} message
   * @param {Record<string, any>} data
   */
  constructor(message, data) {
    super(message);
    this.name = 'ApprovalRequiredError';
    this.status = 202;
    this.approvalRequired = true;
    this.approvalId = data?.approvalId ?? null;
    this.summary = data?.summary ?? null;
    this.reason = data?.reason ?? null;
    this.expiresAt = data?.expiresAt ?? null;
    this.approverAnyOf = data?.approverAnyOf ?? [];
  }
}

export class ApiError extends Error {
  /**
   * @param {string} message
   * @param {number} status
   * @param {unknown} [details]
   */
  constructor(message, status, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

/**
 * @typedef {Object} RequestOptions
 * @property {Record<string, unknown> | FormData | undefined} [body]
 * @property {Record<string, string> | undefined} [headers]
 * @property {AbortSignal | undefined} [signal]
 * @property {boolean | undefined} [skipUnauthorizedRedirect]  Set true for endpoints that may legitimately 401 without bouncing (e.g. probe calls).
 */

/**
 * Core request executor. Components should prefer the verb helpers below.
 *
 * @param {string} method
 * @param {string} path
 * @param {RequestOptions} [opts]
 */
async function request(method, path, opts = {}) {
  const headers = { ...DEFAULT_HEADERS, ...(opts.headers ?? {}) };

  try {
    const token = await acquireApiToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } catch (err) {
    console.error('Failed to acquire API access token:', err);
  }

  /** @type {RequestInit} */
  const init = {
    method,
    headers,
    signal: opts.signal
  };

  if (opts.body !== undefined) {
    if (opts.body instanceof FormData) {
      // Let the browser set the multipart boundary header.
      init.body = opts.body;
    } else {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(opts.body);
    }
  }

  let response;
  try {
    response = await fetch(resolveUrl(path), init);
  } catch (err) {
    // Network-level failure (DNS, offline, CORS, aborted). Also covers the
    // expected case of a beta endpoint not yet existing in apps/api — that
    // surfaces as a normal non-2xx response below, not here.
    if (err && /** @type {any} */ (err).name === 'AbortError') throw err;
    throw new ApiError(
      err instanceof Error ? err.message : 'Network error',
      0,
      err
    );
  }

  if (response.status === 401 && !opts.skipUnauthorizedRedirect) {
    notifyUnauthorized();
    // Throw so callers' .then chains short-circuit instead of receiving
    // an "empty" success.
    throw new ApiError('Session expired', 401);
  }

  // 204 No Content or genuinely empty bodies.
  const contentLength = response.headers.get('content-length');
  const contentType = response.headers.get('content-type') ?? '';
  const noBody = response.status === 204 || contentLength === '0';

  /** @type {any} */
  let payload = undefined;
  if (!noBody) {
    try {
      if (contentType.includes('application/json')) {
        payload = await response.json();
      } else {
        payload = await response.text();
      }
    } catch {
      payload = undefined;
    }
  }

  if (!response.ok) {
    // 404s are expected for the large majority of beta endpoints that don't
    // have an apps/api backing route yet — surface a clean, recognisable
    // ApiError so tabs can render their own empty/error state instead of
    // crashing on an unexpected payload shape.
    const message =
      (payload && typeof payload === 'object' && (payload.error?.message || payload.error || payload.message)) ||
      `HTTP ${response.status} ${response.statusText}`.trim();
    throw new ApiError(message, response.status, payload);
  }

  /**
   * APPROVAL INTERCEPTION — checked BEFORE the envelope unwrap, because this
   * payload carries `ok: false` and would otherwise fall straight through as a
   * plain object that every caller treats as a successful result.
   */
  if (payload && typeof payload === 'object' && payload.approvalRequired === true) {
    throw new ApprovalRequiredError(
      payload.error?.message || 'This needs approval before it can go ahead.',
      payload.data || {}
    );
  }

  // Unwrap Omni Cloud's `{ ok, data }` envelope transparently where present,
  // so ported OmniBeta code that expects the raw payload keeps working.
  if (payload && typeof payload === 'object' && 'ok' in payload && payload.ok === true && 'data' in payload) {
    return payload.data;
  }

  return payload;
}

/** @param {string} path @param {RequestOptions} [opts] */
export const apiGet = (path, opts) => request('GET', path, opts);

/** @param {string} path @param {RequestOptions} [opts] */
export const apiPost = (path, opts) => request('POST', path, opts);

/** @param {string} path @param {RequestOptions} [opts] */
export const apiPut = (path, opts) => request('PUT', path, opts);

/** @param {string} path @param {RequestOptions} [opts] */
export const apiPatch = (path, opts) => request('PATCH', path, opts);

/** @param {string} path @param {RequestOptions} [opts] */
export const apiDelete = (path, opts) => request('DELETE', path, opts);

/**
 * POST a JSON body and return the binary response as a Blob (e.g. a
 * server-generated PDF). Throws ApiError on a non-2xx response.
 * @param {string} path @param {unknown} body @returns {Promise<Blob>}
 */
export async function apiPostBlob(path, body) {
  const headers = { ...DEFAULT_HEADERS, 'Content-Type': 'application/json' };
  try {
    const token = await acquireApiToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (err) {
    console.error('Failed to acquire API access token:', err);
  }
  const response = await fetch(resolveUrl(path), { method: 'POST', headers, body: JSON.stringify(body) });
  if (response.status === 401) {
    notifyUnauthorized();
    throw new ApiError('Session expired', 401);
  }
  if (!response.ok) {
    let message = `HTTP ${response.status} ${response.statusText}`.trim();
    try {
      const j = await response.json();
      message = (j && (j.error?.message || j.error || j.message)) || message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(message, response.status);
  }
  return response.blob();
}

/**
 * GET a binary response as a Blob (e.g. a CSV export).
 *
 * Review finding M6: Audit Logs → Export was a plain `<a href="/api/…">`, so
 * it bypassed API_BASE_URL — a separate origin — and carried no Authorization
 * header, meaning it hit the SWA origin or 401'd. It downloaded nothing. Any
 * authenticated download has to go through fetch with a token; this is that,
 * shared so the next one does not reinvent it.
 *
 * @param {string} path @returns {Promise<Blob>}
 */
export async function apiGetBlob(path) {
  const headers = { ...DEFAULT_HEADERS };
  try {
    const token = await acquireApiToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (err) {
    console.error('Failed to acquire API access token:', err);
  }
  const response = await fetch(resolveUrl(path), { headers });
  if (response.status === 401) {
    notifyUnauthorized();
    throw new ApiError('Session expired', 401);
  }
  if (!response.ok) {
    let message = `HTTP ${response.status} ${response.statusText}`.trim();
    try {
      const j = await response.json();
      message = (j && (j.error?.message || j.error || j.message)) || message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(message, response.status);
  }
  return response.blob();
}

/** Trigger a browser download of a Blob with the given filename. */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Build a query string from an object — omits null/undefined values.
 *
 * @param {Record<string, unknown>} params
 * @returns {string}
 */
export function buildQuery(params) {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      for (const v of value) usp.append(key, String(v));
    } else {
      usp.set(key, String(value));
    }
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}
